import { ArrowRight } from "lucide-react";
import { useApplyIntent } from "@/components/ApplyIntent";
import { useRateCheckRouter } from "@/components/RateCheckRouter";
import { useMLO } from "@/lib/mloContext";
import { resolveApplyUrl } from "@/lib/applyTarget";
import { CONTACT } from "@/lib/constants";

export default function HowItWorks() {
  const { openIntent } = useApplyIntent();
  const { openRouter } = useRateCheckRouter();
  const mlo = useMLO();
  const intentOpts = { applyUrl: resolveApplyUrl(mlo), mloSlug: mlo?.slug, mloName: mlo?.name, mloPhone: mlo?.phone };
  const first = (mlo?.name || CONTACT.loanOfficer).split(" ")[0];
  const full = mlo?.name || CONTACT.loanOfficer;
  const steps = [
    {
      n: "01",
      title: "Share a few basics",
      body: "Answer a short set of questions about the property, your goals, and rough financials. It takes about two minutes and won't affect your credit score.",
    },
    {
      n: "02",
      title: "Get personalized rate scenarios",
      body: "We pull competitive quotes from NEXA Lending's lender network and organize them into clear, side-by-side scenarios — rate, APR, payment, and closing costs.",
    },
    {
      n: "03",
      title: `Talk it through with ${first}`,
      body: `${full} walks you through the numbers, answers questions in plain English, and helps you decide when — and whether — to lock. Zero pressure.`,
    },
  ];
  return (
    <section id="how" data-testid="how-section" className="section-y bg-white relative">
      <div className="container-grc">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">
            How it works
          </p>
          <h2 className="mt-4 font-heading font-medium text-4xl sm:text-5xl text-balance">
            Three simple steps. <span className="italic text-teal">Zero pressure.</span>
          </h2>
        </div>

        <ol className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
          {steps.map((s, i) => (
            <li
              key={s.n}
              data-testid={`how-step-${i + 1}`}
              className="relative rounded-3xl bg-linen/60 border border-linen-border p-8 sm:p-10 overflow-hidden"
            >
              <span
                aria-hidden
                className="absolute -top-6 -left-2 font-heading font-medium text-[8rem] leading-none text-ink-200 select-none pointer-events-none"
              >
                {s.n}
              </span>
              <div className="relative">
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase text-teal">
                  Step {s.n}
                </div>
                <h3 className="mt-4 font-heading font-medium text-2xl">{s.title}</h3>
                <p className="mt-3 text-ink-600 leading-relaxed">{s.body}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute -right-8 top-14 text-teal/50">
                    <ArrowRight size={22} strokeWidth={1.5} />
                  </div>
                )}
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-14 flex flex-col sm:flex-row items-center gap-4">
          <button
            type="button"
            data-testid="how-cta-primary"
            onClick={() => mlo ? openIntent("how-cta-primary", intentOpts) : openRouter("how-cta-primary")}
            className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold px-8 py-4 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
          >
            Check My Rate
            <ArrowRight size={18} />
          </button>
          <p className="text-sm text-ink-500">
            No hard credit pull. Takes about 2 minutes.
          </p>
        </div>
      </div>
    </section>
  );
}
